import React, { useEffect, useState } from 'react';
import { useAppStore } from '../context/AppStore';
import { Card, Button, Badge, Input, EmptyState, ConfirmDialog, Modal, NetworkIcon } from '../components/ui';
import { projectsAPI } from '../api/client';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const TONES   = ['profesional', 'casual', 'inspiracional', 'humoristico'];
const COLORS  = ['#7c3aed','#2563eb','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899','#8b5cf6'];
const MEDIA_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace('/api/v1','');
const STATUS_COLOR = { published:'#10b981', partial:'#f59e0b', failed:'#ef4444', scheduled:'#3b82f6' };
const STATUS_LABEL = { published:'Publicado', partial:'Parcial', failed:'Fallido', scheduled:'Programado' };
const EMPTY_FORM   = { name:'', description:'', tone:'profesional', keywords:'', hashtags:'', color:'#7c3aed' };

/* ── Mini thumbnail ── */
function MiniThumb({ post }) {
  const [err, setErr] = useState(false);
  const url = post.media_url ? `${MEDIA_BASE}${post.media_url}` : null;
  if (!url || err) return (
    <div style={{ width:'48px', height:'48px', borderRadius:'8px', background:'var(--bg-hover)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px', flexShrink:0 }}>
      {post.media_type === 'video' ? '🎬' : '🖼'}
    </div>
  );
  if (post.media_type === 'video') return (
    <div style={{ position:'relative', width:'48px', height:'48px', flexShrink:0 }}>
      <video src={url} style={{ width:'48px', height:'48px', objectFit:'cover', borderRadius:'8px', display:'block' }} muted preload="metadata" onError={() => setErr(true)} />
      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'8px', background:'rgba(0,0,0,0.4)', fontSize:'14px', color:'#fff' }}>▶</div>
    </div>
  );
  return <img src={url} alt="media" onError={() => setErr(true)} style={{ width:'48px', height:'48px', objectFit:'cover', borderRadius:'8px', flexShrink:0 }} />;
}

/* ── Form ── */
function ProjectForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = k => v => setForm(f => ({ ...f, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); if (!form.name.trim()) { toast.error('El nombre es requerido'); return; } onSave(form); }} style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
      <Input label="Nombre del proyecto" value={form.name} onChange={set('name')} placeholder="Ej: SaaS Dashboard Pro" required icon="📁" />
      <Input label="Descripción" value={form.description} onChange={set('description')} placeholder="¿De qué trata este proyecto?" multiline rows={3} />
      <Input label="Palabras clave" value={form.keywords} onChange={set('keywords')} placeholder="SaaS, analytics, empresas" hint="La IA las usará para generar contenido más preciso" />
      <Input label="Hashtags base" value={form.hashtags} onChange={set('hashtags')} placeholder="#SaaS #Tech #Software" icon="#" />
      <div>
        <label style={{ fontSize:'13px', fontWeight:'600', color:'var(--text-secondary)', display:'block', marginBottom:'8px' }}>Tono de voz</label>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {TONES.map(t => (
            <button type="button" key={t} onClick={() => set('tone')(t)} style={{ padding:'7px 14px', borderRadius:'8px', border:`1px solid ${form.tone===t?'#7c3aed':'var(--border)'}`, background:form.tone===t?'#7c3aed20':'var(--bg-elevated)', color:form.tone===t?'#a78bfa':'var(--text-muted)', fontSize:'13px', fontFamily:'var(--font-body)', cursor:'pointer', textTransform:'capitalize' }}>{t}</button>
          ))}
        </div>
      </div>
      <div>
        <label style={{ fontSize:'13px', fontWeight:'600', color:'var(--text-secondary)', display:'block', marginBottom:'8px' }}>Color</label>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {COLORS.map(c => (
            <button type="button" key={c} onClick={() => set('color')(c)} style={{ width:'32px', height:'32px', borderRadius:'50%', background:c, border:`3px solid ${form.color===c?'#fff':'transparent'}`, cursor:'pointer', boxShadow:form.color===c?`0 0 0 2px ${c}`:'none' }} />
          ))}
        </div>
      </div>
      <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', paddingTop:'8px' }}>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={loading}>{initial ? 'Guardar cambios' : 'Crear proyecto'}</Button>
      </div>
    </form>
  );
}

/* ── Project detail drawer ── */
function ProjectDetail({ project, onClose }) {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectsAPI.posts(project.id)
      .then(r => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [project.id]);

  const totalLikes   = posts.reduce((a, p) => a + p.networks.reduce((b, n) => b + (n.likes    || 0), 0), 0);
  const totalReach   = posts.reduce((a, p) => a + p.networks.reduce((b, n) => b + (n.reach    || 0), 0), 0);
  const totalOk      = posts.filter(p => p.status === 'published').length;
  const fmt = v => v > 999 ? (v/1000).toFixed(1)+'K' : v;

  return (
    <div className="animate-slideInRight" style={{
      position:'fixed', top:0, right:0, bottom:0, width:'min(420px, 100vw)',
      background:'var(--bg-surface)', borderLeft:'1px solid var(--border)',
      zIndex:300, display:'flex', flexDirection:'column', overflowY:'auto',
    }}>
      {/* Header */}
      <div style={{ padding:'20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'12px', flexShrink:0 }}>
        <div style={{ width:'40px', height:'40px', borderRadius:'10px', background:`${project.color}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px', flexShrink:0 }}>📁</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:'var(--font-display)', fontWeight:'800', fontSize:'16px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{project.name}</div>
          <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>{project.description || 'Sin descripción'}</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'20px', padding:'4px' }}>✕</button>
      </div>

      {/* Stats */}
      <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', flexShrink:0 }}>
        {[
          { label:'Posts',    val: posts.length, icon:'📤' },
          { label:'Likes',    val: fmt(totalLikes), icon:'❤️' },
          { label:'Alcance',  val: fmt(totalReach), icon:'👁' },
        ].map(s => (
          <div key={s.label} style={{ textAlign:'center', padding:'12px', borderRadius:'var(--radius-md)', background:'var(--bg-elevated)' }}>
            <div style={{ fontSize:'18px', marginBottom:'4px' }}>{s.icon}</div>
            <div style={{ fontWeight:'800', fontSize:'18px' }}>{s.val}</div>
            <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Info */}
      <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:'8px', flexShrink:0 }}>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          <Badge color={project.color}>{project.tone}</Badge>
          {project.hashtags && project.hashtags.split(' ').slice(0,3).map(h => <Badge key={h} color="#555">{h}</Badge>)}
        </div>
        {project.keywords && <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>🔑 {project.keywords}</div>}
      </div>

      {/* Publicaciones */}
      <div style={{ flex:1, padding:'16px 20px', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
          <h4 style={{ fontFamily:'var(--font-display)', fontWeight:'700', fontSize:'14px' }}>Publicaciones ({posts.length})</h4>
          <Link to={`/publish`} style={{ textDecoration:'none' }}>
            <Button size="sm" icon="⚡">Nueva</Button>
          </Link>
        </div>

        {loading ? (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {[...Array(3)].map((_,i) => <div key={i} className="skeleton" style={{ height:'70px', borderRadius:'var(--radius-md)' }} />)}
          </div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text-muted)', fontSize:'13px' }}>
            📭 Sin publicaciones en este proyecto.<br />
            <Link to="/publish" style={{ color:'var(--accent-primary)' }}>Crear la primera →</Link>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {posts.map(post => (
              <div key={post.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 12px', borderRadius:'var(--radius-md)', background:'var(--bg-elevated)', border:'1px solid var(--border)' }}>
                <MiniThumb post={post} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'3px', flexWrap:'wrap' }}>
                    {post.networks.map(n => <NetworkIcon key={n.network} network={n.network} size={16} />)}
                    <Badge color={STATUS_COLOR[post.status]}>{STATUS_LABEL[post.status]}</Badge>
                  </div>
                  <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                    {new Date(post.created_at).toLocaleDateString('es', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </div>
                  {post.networks.find(n => n.caption) && (
                    <p style={{ fontSize:'11px', color:'var(--text-secondary)', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {post.networks.find(n => n.caption)?.caption}
                    </p>
                  )}
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:'12px', fontWeight:'700' }}>❤️ {post.networks.reduce((a,n)=>a+(n.likes||0),0)}</div>
                  <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>👁 {fmt(post.networks.reduce((a,n)=>a+(n.reach||0),0))}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function ProjectsPage() {
  const { projects, projectsLoading, fetchProjects, addProject, updateProject, deleteProject } = useAppStore();
  const [showForm,  setShowForm]  = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [deleting,  setDeleting]  = useState(null);
  const [detail,    setDetail]    = useState(null);
  const [saving,    setSaving]    = useState(false);

  useEffect(() => { fetchProjects(); }, []);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) { await updateProject(editing.id, form); toast.success('Proyecto actualizado ✓'); }
      else         { await addProject(form);                 toast.success('Proyecto creado ✓'); }
      setShowForm(false); setEditing(null);
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
        <p style={{ color:'var(--text-muted)', fontSize:'14px' }}>{projects.length} proyectos · Haz clic en uno para ver sus publicaciones</p>
        <Button icon="+" onClick={() => { setEditing(null); setShowForm(true); }}>Nuevo proyecto</Button>
      </div>

      {projectsLoading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'16px' }}>
          {[...Array(3)].map((_,i) => <div key={i} className="skeleton" style={{ height:'180px', borderRadius:'var(--radius-lg)' }} />)}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState icon="📁" title="Sin proyectos" description="Crea tu primer proyecto para organizar tus publicaciones por marca o campaña." action={<Button icon="+" onClick={() => setShowForm(true)}>Crear proyecto</Button>} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:'16px' }}>
          {projects.map((p, i) => (
            <Card
              key={p.id} hoverable
              className={`animate-fadeIn delay-${i+1} card-touch`}
              style={{ borderTop:`3px solid ${p.color}`, cursor:'pointer' }}
              onClick={() => setDetail(p)}
            >
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'12px' }}>
                <div style={{ width:'40px', height:'40px', borderRadius:'10px', background:`${p.color}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px' }}>📁</div>
                <div style={{ display:'flex', gap:'6px' }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => { setEditing(p); setShowForm(true); }} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', color:'var(--text-muted)', cursor:'pointer', padding:'5px 8px', borderRadius:'7px', fontSize:'13px' }}>✏️</button>
                  <button onClick={() => setDeleting(p)} style={{ background:'#ef444415', border:'1px solid #ef444430', color:'#ef4444', cursor:'pointer', padding:'5px 8px', borderRadius:'7px', fontSize:'13px' }}>🗑</button>
                </div>
              </div>

              <h3 style={{ fontFamily:'var(--font-display)', fontSize:'16px', fontWeight:'700', marginBottom:'4px' }}>{p.name}</h3>
              <p style={{ color:'var(--text-muted)', fontSize:'13px', lineHeight:1.5, marginBottom:'12px', minHeight:'38px', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
                {p.description || 'Sin descripción'}
              </p>

              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                  <Badge color={p.color}>{p.tone}</Badge>
                  {p.hashtags && <Badge color="#555">{p.hashtags.split(' ')[0]}</Badge>}
                </div>
                <div style={{ fontSize:'12px', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:'4px' }}>
                  <span style={{ fontWeight:'700', color:'var(--text-primary)' }}>{p.posts_count || 0}</span> posts
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Overlay */}
      {detail && <div onClick={() => setDetail(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:299, backdropFilter:'blur(3px)' }} />}
      {detail && <ProjectDetail project={detail} onClose={() => setDetail(null)} />}

      <Modal open={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing ? 'Editar proyecto' : 'Nuevo proyecto'} width={520}>
        <ProjectForm initial={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} loading={saving} />
      </Modal>

      <ConfirmDialog
        open={!!deleting} onClose={() => setDeleting(null)}
        onConfirm={() => { deleteProject(deleting?.id); toast.success('Proyecto eliminado'); setDeleting(null); }}
        title="Eliminar proyecto"
        message={`¿Eliminar "${deleting?.name}"? Las publicaciones asociadas no se eliminarán.`}
        confirmLabel="Eliminar" danger
      />
    </div>
  );
}
